/**
 * Lightweight client-side bot deterrent for public auth forms.
 *
 * Two cheap signals, no third-party captcha and no extra infrastructure:
 *  1. A honeypot field that is invisible and unfocusable for people, but that
 *     naive form-filling bots populate.
 *  2. A minimum time-to-submit. A human needs a couple of seconds to type an
 *     email and a password; scripted submits land almost instantly.
 *
 * This raises the cost of drive-by abuse. It is not a defence against a
 * targeted attacker, and it never blocks a real person with a visible error:
 * a tripped guard fails quietly with the same generic message.
 */
import { useCallback, useRef } from "react";

/** Submissions faster than this are treated as scripted. */
export const MIN_HUMAN_FILL_MS = 1500;

export type BotGuard = {
  /** Spread onto a hidden text input rendered inside the form. */
  honeypotProps: {
    name: string;
    type: "text";
    tabIndex: -1;
    autoComplete: "off";
    "aria-hidden": true;
    className: string;
    ref: React.RefObject<HTMLInputElement>;
    defaultValue: string;
  };
  /** True when the submission looks automated. */
  isLikelyBot: () => boolean;
};

export function useBotGuard(): BotGuard {
  const honeypotRef = useRef<HTMLInputElement>(null);
  const mountedAt = useRef<number>(Date.now());

  const isLikelyBot = useCallback(() => {
    if (honeypotRef.current?.value) return true;
    return Date.now() - mountedAt.current < MIN_HUMAN_FILL_MS;
  }, []);

  return {
    honeypotProps: {
      name: "company_website",
      type: "text",
      tabIndex: -1,
      autoComplete: "off",
      "aria-hidden": true,
      className: "absolute left-[-9999px] top-0 h-px w-px opacity-0 pointer-events-none",
      ref: honeypotRef,
      defaultValue: "",
    },
    isLikelyBot,
  };
}

/** Renders the honeypot input. Place it anywhere inside the guarded form. */
export function HoneypotField({ guard }: { guard: BotGuard }) {
  return <input {...guard.honeypotProps} />;
}
